import { useTranslation } from "react-i18next";

/** Renders nothing for terminal reports (sla_breach_at: null) and a
 * colour-coded "X left" / "overdue X" pill for open ones. Tones:
 *   - red:   already breached
 *   - amber: < 24h until breach
 *   - slate: > 24h until breach
 *
 * Deliberately not auto-ticking — re-render on the route changes that
 * already happen often enough. A misread minute on a 7-day SLA doesn't
 * matter.
 */
export function SlaBadge({ breachAt }: { breachAt: string | null }) {
  const { t } = useTranslation();
  if (!breachAt) return null;
  const diffMs = new Date(breachAt).getTime() - Date.now();
  const overdue = diffMs < 0;
  const value = formatShort(Math.abs(diffMs), t);
  const label = overdue
    ? t("sla.overdue", { value })
    : t("sla.left", { value });
  const tone = overdue
    ? "bg-rose-100 text-rose-700"
    : diffMs < 24 * 60 * 60 * 1000
      ? "bg-amber-100 text-amber-700"
      : "bg-slate-100 text-slate-600";
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
    >
      {label}
    </span>
  );
}

function formatShort(
  ms: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return t("sla.days_short", { count: days });
  }
  return t("sla.hours_short", { count: Math.max(1, hours) });
}
